import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { ReactComponent as ArrowLeftIcon } from '../icons/arrow.left.svg';
import { ReactComponent as ArrowRightIcon } from '../icons/arrow.right.svg';
import { ReactComponent as XmarkCircleIcon } from '../icons/xmark.circle.svg';
import { searchArtistAndMember } from '../data/repositories/SearchArtistAndMember';
import type { Artist, Member } from '../types/response/ISearchArtistResponse';
import { addFan } from '../data/repositories/AddFan';
import { getUserLocale } from '../utils/getUserLocale';
import { searchAllArtist } from '../data/repositories/SearchAllArtist';
import { debounce } from 'lodash';
import { createArtist } from '../data/repositories/CreateArtist';
import { useColorMode } from '@chakra-ui/react';
import NavigateArtistSelectButton from '../presentation/select-artist/NavigateArtistSelectButton';
import saveData from '../utils/saveData';

const placeholder = t('newSearchArtistsPage.placeholder');

interface SearchResultBoxProps {
  name: string;
  subName?: string;
  artistIds: string[];
  memberId?: string;
  handleAddFan: (artistIds: string[], memberId?: string) => Promise<void>;
}
interface RequestArtistModalProps {
  onClose: () => void;
  onSubmit: (artist: string) => Promise<Response>;
  onSuccess: () => void;
}

function SearchResultBox({
  name,
  subName,
  handleAddFan,
  artistIds,
  memberId
}: SearchResultBoxProps) {
  return (
    <div className="w-full h-[84px] p-[20px] bg-background rounded-[12px] backdrop-blur-[20px] flex justify-start items-center gap-[10px] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.04),_0px_0px_24px_0px_rgba(255,255,255,0.30)_inset] dark:shadow-[0px_4px_10px_0px_rgba(0,0,0,0.43),_0px_0px_24px_0px_rgba(34,34,34,0.26)_inset]">
      <div className="flex-1 flex justify-between items-center">
        <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
          <span className="self-stretch justify-start text-main-light-1 dark:text-main-dark-1 text-[17px] font-semibold">
            {t('newSearchArtistsPage.searchResultBox.name', { name })}
          </span>
          {subName && (
            <span className="self-stretch justify-start text-main-light-4 dark:text-main-dark-4 text-[13px] font-normal">
              {t('newSearchArtistsPage.searchResultBox.subName', { subName })}
            </span>
          )}
        </div>
        <div className="flex justify-start items-center">
          <button
            className="w-[89px] h-[39px] px-[18px] py-[11px] bg-primary-light dark:bg-primary-dark rounded-[100px] flex justify-center items-center gap-1"
            onClick={() => handleAddFan(artistIds, memberId)}
            type="button"
          >
            <div className="text-center justify-start text-onPrimary text-[14px] font-semibold">
              {t('newSearchArtistsPage.searchResultBox.buttonText')}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestArtistModal({ onClose, onSubmit, onSuccess }: RequestArtistModalProps) {
  const [content, setContent] = useState('');

  const handleOverlayClick = () => {
    onClose();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  // 요청하기 로직
  const handleSubmit = async () => {
    if (!content.trim()) {
      alert(t('newSearchArtistsPage.requestArtistModal.emptyAlert'));
      return;
    }
    try {
      const result = await onSubmit(content.trim());
      if (result.ok) {
        onSuccess();
      } else {
        throw new Error('Failed to create artist');
      }
    } catch (error) {
      console.error(error);
      alert(t('newSearchArtistsPage.requestArtistModal.errorAlert'));
    } finally {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-45"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white dark:bg-backgroundElevated-dark rounded-[24px] w-full mx-[32px] max-w-[400px] pt-[32px] pb-[16px] px-[14px] flex flex-col "
        onClick={handleModalClick}
      >
        <span className="text-[17px] font-bold leading-[22px] whitespace-pre-line text-center mb-[8px]">
          {t('newSearchArtistsPage.requestArtistModal.title')}
        </span>
        <span className="text-[15px] text-center text-main-light-4 dark:text-main-dark-4 mb-[32px]">
          {t('newSearchArtistsPage.requestArtistModal.description')}
        </span>
        <input
          type="text"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          className="
            self-stretch
            bg-secondary-light dark:bg-secondary-dark
            rounded-[12px]
            h-[54px]
            py-[18px] px-[16px]
            text-gray-900 dark:text-gray-100
            placeholder-gray-400 dark:placeholder-gray-500
            focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark
            border border-transparent mb-6
          "
        />
        <div className="flex w-full gap-[8px] mt-[8px]">
          <button
            type="button"
            className="
              h-[52px]
              flex-1
              px-[24px] py-auto
              bg-secondary-light dark:bg-secondary-dark 
              text-onSecondary-light dark:text-onSecondary-dark
              rounded-[100px]
              font-semibold
              text-[17px]
            "
            onClick={onClose}
          >
            {t('newSearchArtistsPage.requestArtistModal.cancelButtonText')}
          </button>
          <button
            type="button"
            className="
              h-[52px]
              flex-1
              px-[24px] py-auto
              bg-primary-light dark:bg-primary-dark 
              text-white
              font-semibold
              text-[17px]
              rounded-[100px]
            "
            onClick={handleSubmit}
          >
            {t('newSearchArtistsPage.requestArtistModal.submitButtonText')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SearchArtistsPage() {
  const [input, setInput] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [allArtists, setAllArtists] = useState<Artist[]>([]);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [groupedMembers, setGroupedMembers] = useState<
    Record<string, { member: Member; artists: Artist[] }>
  >({});
  const [locale, setLocale] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const notInitial = queryParams.get('notInitial');
  const callbackUrl = queryParams.get('callback') ?? '';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value);

  useEffect(() => {
    setIsSuccessModalOpen(false);
  }, []);

  const onBackClick = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      return;
    }
  };

  // 검색 결과 로직
  const handleSearchArtist = (input: string) => {
    const result = searchArtistAndMember(input, allArtists);
    const artists: Artist[] = result.artists;
    const members: Array<{ member: Member; artist: Artist }> = result.members;

    const groupedMembers: Record<string, { member: Member; artists: Artist[] }> = members.reduce(
      (acc, { member, artist }) => {
        const key = member.memberId;
        if (!acc[key]) acc[key] = { member, artists: [] };
        acc[key].artists.push(artist);
        return acc;
      },
      {} as Record<string, { member: Member; artists: Artist[] }>
    );
    setArtists(artists);
    setGroupedMembers(groupedMembers);
  };

  // 팬 추가 로직
  const handleAddFan = async (artistIds: string[], memberId?: string) => {
    const result = await addFan({ memberId, artistIds });
    saveData('artist', artistIds[0]);
    if (memberId) {
      saveData('memberId', memberId);
    }
    if (result) {
      if (notInitial) {
        navigate(`/add-artists-complete${callbackUrl && `?callback=${callbackUrl}`}`, {
          replace: true
        });
      } else {
        navigate('/signup-complete', { replace: true });
      }
    } else {
      alert(t('newSearchArtistsPage.searchResultBox.requestErrorAlert'));
    }
  };

  const clearInput = () => {
    setInput('');
  };

  const debouncedSearch = debounce((input: string) => {
    handleSearchArtist(input);
  }, 200);

  useEffect(() => {
    if (allArtists && allArtists.length === 0) return;
    debouncedSearch(input);
    return () => debouncedSearch.cancel();
  }, [input]);

  useEffect(() => {
    const getLocale = getUserLocale();
    setLocale(getLocale);
    const getallArtists = async () => {
      const allArtists = await searchAllArtist();
      setAllArtists(allArtists);
      if (allArtists && allArtists.length > 0) {
        setIsDisabled(false);
      }
    };
    getallArtists();
  }, []);

  return (
    <div className="flex flex-col min-h-screen pt-[calc(env(safe-area-inset-top))] pb-[calc(env(safe-area-inset-bottom))] items-center w-[100vw] max-w-[570px]">
      {notInitial ? (
        <button
          className="mt-[14px] px-[16px] mb-[15px] w-full z-50 justify-start"
          onClick={onBackClick}
          type="button"
        >
          <ArrowLeftIcon className="fill-black dark:fill-white" />
        </button>
      ) : (
        <div className="h-[54px]" />
      )}
      <div className="flex flex-col items-center w-[100vw] max-w-[570px] px-[24px]">
        <div className="flex flex-col w-full py-[10px]">
          <span className="text-left text-[24px] font-bold line-height-[120%]">
            {t('newSearchArtistsPage.title')}
          </span>
          {input ? (
            <div className="flex flex-row items-center gap-[4px] mt-[8px]">
              <img
                src="/assets/icons/graphic-3d/3D.like.png"
                alt="like"
                className="w-[18px] h-[18px]"
              />
              <span className="text-main-light-4 dark:text-main-dark-4 text-[15px] break-keep">
                {t('newSearchArtistsPage.selectDescription')}
              </span>
            </div>
          ) : (
            <span className="text-main-light-4 dark:text-main-dark-4 mt-2 text-[15px] break-keep">
              {t('newSearchArtistsPage.description')}
            </span>
          )}
        </div>
        <div className="relative mt-[28px] w-full">
          <input
            type="text"
            value={input}
            disabled={isDisabled}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="
                w-full
                bg-secondary-light dark:bg-secondary-dark
                rounded-[12px]
                py-[14px] px-[16px]
                text-gray-900 dark:text-gray-100
                placeholder-gray-400 dark:placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark
                border
                border-transparent
              "
          />
          {input && (
            <button
              type="button"
              onClick={clearInput}
              className="
                  absolute right-[16px] top-1/2
                  -translate-y-1/2
                  w-[24px] h-[24px]
                  flex items-center justify-center
                "
            >
              <XmarkCircleIcon fill="gray" width={24} height={24} />
            </button>
          )}
        </div>
        <div className="w-full flex flex-col justify-center items-center max-w-[570px] mt-[24px] gap-[16px]">
          {artists?.length > 0 &&
            artists.map((artist) => (
              <SearchResultBox
                key={artist.artistId}
                name={artist.name}
                artistIds={[artist.artistId]}
                handleAddFan={handleAddFan}
              />
            ))}
          {groupedMembers &&
            Object.values(groupedMembers).map(({ member, artists }) => {
              const name = member.translations.name![locale!] || member.name;
              return (
                <SearchResultBox
                  key={member.memberId}
                  memberId={member.memberId}
                  name={name}
                  subName={artists.map((artist) => artist.name).join(', ')}
                  artistIds={artists.map((artist) => artist.artistId)}
                  handleAddFan={handleAddFan}
                />
              );
            })}
        </div>
        {input && (
          <button
            className="mt-[28px] mb-[50px] flex flex-row items-center gap-[4px]"
            onClick={() => setIsModalOpen(true)}
            type="button"
          >
            <span className="text-main-light-3 font-normal text-[13px] dark:text-main-dark-3">
              {t('newSearchArtistsPage.requestAddArtist')}
            </span>
            <ArrowRightIcon className="fill-main-light-3 dark:fill-main-dark-3 size-[13px]" />
          </button>
        )}
      </div>

      {isModalOpen && (
        <RequestArtistModal
          onClose={() => setIsModalOpen(false)}
          onSubmit={createArtist}
          onSuccess={() => setIsSuccessModalOpen(true)}
        />
      )}

      {isSuccessModalOpen && (
        <SuccessRequestArtistPopup onClick={() => setIsSuccessModalOpen(false)} />
      )}
    </div>
  );
}

const SuccessRequestArtistPopup = ({ onClick }: { onClick: () => void }) => {
  const { colorMode } = useColorMode();
  return (
    <div
      className="min-h-screen fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{
        backgroundImage: `url(/assets/force_to_update/bg_ask_update_${colorMode}.png)`,
        backgroundRepeat: 'round'
      }}
    >
      <div className="flex flex-col items-center justify-center">
        <img
          src="/assets/icons/incoming_envelopment.png"
          alt="incoming_envelopment"
          className="w-[191px] h-[115px]"
        />
        <div className="py-[10px] px-[24px] flex flex-col gap-[14px]">
          <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] text-center">
            {t('successRequestArtistPopup.title')}
          </h1>
          <p className="text-[15px] font-medium text-text-main-lv4 text-center whitespace-pre-line">
            {t('successRequestArtistPopup.description')}
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center bg-background-light dark:bg-background-dark rounded-[20px] p-[16px] mt-[50px] z-[60] shadow-[0_0_1px_rgba(142,127,221,0.05),_0_0_18px_0_rgba(179,171,222,0.32)] w-[calc(100vw-48px)] h-[169px] justify-center">
        <p className="text-center text-[15px] font-semibold text-text-main-lv2 leading-[18px] mb-[24px] whitespace-pre-line">
          {t('successRequestArtistPopup.selectDescription')}
        </p>
        <NavigateArtistSelectButton onClick={onClick} />
      </div>
    </div>
  );
};
